import React, { useEffect, useState } from "react";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import {
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  Row,
  Icon,
  Block,
} from "../../components/Component";
import { Card } from "reactstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { getOrderDetail } from "../../slice/order-detail";
import { deleteOrder } from "../../api/order";
import DeleteConfirmationModal from "../admin/modals/DeleteConfimationModal";
import {
  formatDateTime,
  formatTotal,
  paymentMethodLabel,
  statusColor,
  statusLabel,
  userName,
} from "./order-utils";

const renderItem = (label, value) => (
  <div className="d-flex justify-content-between align-items-center py-2">
    <span className="text-muted">{label}</span>
    <span className="fw-bold text-end text-break">{value || "N/A"}</span>
  </div>
);

const UserCard = ({ title, user }) => (
  <Card className="h-100">
    <div className="card-inner">
      <h5 className="card-title mb-3">{title}</h5>
      {user ? (
        <>
          {renderItem(
            "Name",
            <Link to={`/users/${user._id}`}>{userName(user)}</Link>
          )}
          {renderItem("Email", user.email)}
        </>
      ) : (
        <div className="text-muted">No data</div>
      )}
    </div>
  </Card>
);

const optionsText = (options) =>
  (options ?? [])
    .map((o) => [o?.name, o?.value].filter(Boolean).join(": "))
    .filter(Boolean)
    .join(", ");

const OrderDetailPage = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isDeleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    dispatch(getOrderDetail(id));
  }, [id, dispatch]);

  const { order, loading } = useSelector((state) => state.orderDetail);
  // The slice keeps the previously viewed order until the new one arrives.
  const current = order?._id === id ? order : null;
  const shipping = current?.shippingInfo ?? {};

  const onDelete = async () => {
    try {
      await deleteOrder(id);
      toast.success("Delete order successfully");
      navigate("/orders");
    } catch (error) {
      toast.error(error);
    }
  };

  return (
    <React.Fragment>
      <Head title="Order Detail"></Head>
      <Content>
        <BlockHead size="sm">
          <BlockBetween className="g-3">
            <BlockHeadContent>
              <BlockTitle>
                Order {current?.code ?? ""}
              </BlockTitle>
            </BlockHeadContent>
            <BlockHeadContent>
              {current && (
                <Button
                  color="danger"
                  className="me-2"
                  onClick={() => setDeleteOpen(true)}
                >
                  <Icon name="trash"></Icon>
                  <span className="d-none d-sm-inline">Delete</span>
                </Button>
              )}
              <Link to={`/orders`}>
                <Button
                  color="light"
                  outline
                  className="bg-white d-none d-sm-inline-flex"
                >
                  <Icon name="arrow-left"></Icon>
                  <span>Back</span>
                </Button>
              </Link>
              <Link to={`/orders`}>
                <Button
                  color="light"
                  outline
                  className="btn-icon bg-white d-inline-flex d-sm-none"
                >
                  <Icon name="arrow-left"></Icon>
                </Button>
              </Link>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        {!current && loading && <div className="text-muted">Loading...</div>}

        {current && (
          <Block>
            <Row className="g-gs">
              <Col lg={6}>
                <Card className="h-100">
                  <div className="card-inner">
                    <h5 className="card-title mb-3">Order Information</h5>
                    {renderItem("Code", current.code)}
                    {renderItem(
                      "Status",
                      <span
                        className={`badge badge-dim bg-outline-${statusColor(
                          current.status
                        )}`}
                      >
                        {statusLabel(current.status)}
                      </span>
                    )}
                    {renderItem(
                      "Payment Method",
                      paymentMethodLabel(current.paymentMethod)
                    )}
                    {renderItem(
                      "Total",
                      formatTotal(current.total, current.currency)
                    )}
                    {renderItem("Created At", formatDateTime(current.createdAt))}
                  </div>
                </Card>
              </Col>
              <Col lg={6}>
                <Card className="h-100">
                  <div className="card-inner">
                    <h5 className="card-title mb-3">Shipping Information</h5>
                    {renderItem("Phone", shipping.phone)}
                    {renderItem("Address", shipping.address)}
                    {renderItem("City", shipping.city)}
                    {renderItem("State", shipping.state)}
                    {renderItem("Country", shipping.country)}
                    {renderItem("Zip Code", shipping.zipCode)}
                  </div>
                </Card>
              </Col>
              <Col lg={6}>
                <UserCard title="Buyer" user={current.user} />
              </Col>
              <Col lg={6}>
                <UserCard title="Seller" user={current.seller} />
              </Col>
              <Col lg={6}>
                <Card className="h-100">
                  <div className="card-inner">
                    <h5 className="card-title mb-3">Items</h5>
                    <div className="table-responsive">
                      <table className="table table-middle mb-0">
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>Options</th>
                            <th className="text-end">Quantity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(current.items ?? []).map((item) => (
                            <tr key={item._id}>
                              <td>
                                <div className="d-flex align-items-center">
                                  {item.image && (
                                    <img
                                      src={item.image}
                                      alt=""
                                      style={{
                                        width: 48,
                                        height: 48,
                                        objectFit: "cover",
                                        borderRadius: 6,
                                        marginRight: 12,
                                      }}
                                    />
                                  )}
                                  <span>{item.name}</span>
                                </div>
                              </td>
                              <td>{optionsText(item.options) || "-"}</td>
                              <td className="text-end">{item.quantity}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </Card>
              </Col>
              <Col lg={6}>
                <Card className="h-100">
                  <div className="card-inner">
                    <h5 className="card-title mb-3">Status History</h5>
                    {(current.notes ?? []).length === 0 ? (
                      <div className="text-muted">No history</div>
                    ) : (
                      (current.notes ?? []).map((note) => (
                        <div
                          key={note._id}
                          className="d-flex justify-content-between py-2 border-bottom"
                        >
                          <div>
                            <div className="fw-bold">{note.title}</div>
                            <div className="text-muted">{note.desc}</div>
                          </div>
                          <span className="text-muted text-nowrap ms-3">
                            {formatDateTime(note.createdAt)}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </Card>
              </Col>
            </Row>
          </Block>
        )}
      </Content>
      <DeleteConfirmationModal
        title="Delete this order?"
        desc="The order, its items and status history will be permanently deleted."
        isOpen={isDeleteOpen}
        onCloseModal={() => setDeleteOpen(false)}
        onConfirm={onDelete}
      />
    </React.Fragment>
  );
};

export default OrderDetailPage;
